import { useMutation, useQuery } from '@apollo/client';
import {
  AddContactIcon,
  AlertService,
  Button,
  CheckboxWithLabel,
  Loading,
  LoadingLarge,
  LoadingLine,
  MiniTooltip,
  Search,
  SelectOption,
  TextInput,
  Theme,
  Timing,
} from '@spoiler-alert/ui-library';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import MemorySelect from '../../components/forms/memory-select';
import {
  AddSellerContactToCuratedList,
  CloneCuratedList,
  SaveCuratedListSettings,
  ToggleBuyerInCuratedList,
} from '../../graphql/mutations';
import { CuratedListByCustomerQuery, FeatureFlagQuery, SitesByRoleAndType } from '../../graphql/queries';
import { TitleService } from '../../services';
import { Breadcrumbs } from '../../store';
import BuyerSelection from './buyer-selection';
import EditContactModal from './edit-contact-modal';
import { BSR, CuratedListMutationResult, DistributionList, RootBSR, SelectItem, Site } from './curated-list-types';

const useStyles = createUseStyles(
  {
    wrap: {
      '& h4': {
        margin: [5, 0, 5],
      },
    },
    headerRow: {
      display: 'flex',
      width: '100%',
      alignItems: 'center',
      gap: 15,
      fontSize: 16,
      flexWrap: 'wrap',
    },
    select: {
      flexGrow: 1,
      marginRight: 'auto',
      maxWidth: 300,
    },
    noCuratedList: {
      textAlign: 'center',
      fontSize: 15,
      padding: 5,
      flex: '1 1 400px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    noCuratedListContent: {
      background: Theme.grey10,
      padding: 24,
      borderRadius: 12,
    },
    listSettings: {
      margin: [0, 0, 0, 'auto'],
      display: 'flex',
      gap: 15,
      alignItems: 'center',
    },
    listNameField: {
      width: 250,
    },
    buyerFilter: {
      display: 'flex',
      gap: 15,
      alignItems: 'center',
      flexWrap: 'wrap',
      background: Theme.white,
      padding: [6, 15],
      borderRadius: 10,
      boxShadow: '0 0 5px rgba(0,0,0,0.3)',
      position: 'relative',
    },
    workingArea: {
      display: 'flex',
      width: '100%',
      minHeight: 'calc(100vh - 130px)',
      flexDirection: 'column',
      gap: 15,
      padding: 10,
      marginTop: 10,
      background: Theme.grey5,
      borderRadius: 15,
      boxShadow: 'inset 0 0 5px rgba(0,0,0,0.3)',
    },
  },
  { name: 'CuratedDistributionLists' },
);

const CuratedDistributionLists = () => {
  const classes = useStyles();

  const [siteSelection, setSiteSelection] = useState<SelectItem[]>([]);
  const [addingSellerContact, setAddingSellerContact] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [editingList, setEditingList] = useState<DistributionList<BSR>>();

  const { data: featureFlagData, loading: loadingFeatureFlag } = useQuery<
    {
      getFeatureFlagQuery: {
        _id: string;
        featureName: string;
        siteIds: string[];
        enabled: boolean;
        excludedSubscriptions: string[];
      };
    },
    { featureName: string }
  >(FeatureFlagQuery, {
    variables: {
      featureName: 'CERTIFIED BUYERS DL',
    },
  });

  const allowedSellerSiteIds = useMemo(() => {
    if (featureFlagData?.getFeatureFlagQuery) {
      return featureFlagData?.getFeatureFlagQuery.siteIds;
    }
    return [];
  }, [featureFlagData]);

  const { data: sitesData, loading: loadingSites } = useQuery<{ adminSitesQuery: Site[] }>(SitesByRoleAndType, {
    variables: {
      siteRoles: ['SELLER'],
      siteTypes: ['ORGANIZATION'],
    },
  });

  const { data: listData, loading: loadingLists } = useQuery<{
    adminCuratedListByCustomerQuery: {
      curatedLists: DistributionList<BSR>[];
      defaultList: DistributionList<RootBSR>;
    };
  }>(CuratedListByCustomerQuery, {
    variables: {
      sellerSiteId: siteSelection[0]?.value,
    },
    skip: siteSelection.length === 0,
  });
  const [cloneList, { loading: cloning }] = useMutation<
    { cloneCuratedList: CuratedListMutationResult },
    {
      sellerSiteId: string;
      curatedListId: string;
    }
  >(CloneCuratedList);
  const [toggleBuyers, { loading: loadingToggleBuyer }] = useMutation<
    { toggleBuyerInCuratedList: CuratedListMutationResult },
    {
      curatedListId: string;
      sellerSiteId: string;
      buyerSiteIds: string[];
      enable: boolean;
    }
  >(ToggleBuyerInCuratedList);
  const [addSellerEmail, { loading: addingSellerEmail }] = useMutation<
    { addSellerContact: CuratedListMutationResult },
    {
      curatedListId: string;
      sellerUserEmail: string;
    }
  >(AddSellerContactToCuratedList);

  const [saveSettings, { loading: savingSettings }] = useMutation<
    { saveCuratedListSettings: CuratedListMutationResult },
    {
      curatedListId: string;
      name: string;
      archived: boolean;
      sellerSiteId: string;
    }
  >(SaveCuratedListSettings);

  const loadingInitial = loadingFeatureFlag || loadingSites;

  const loadingInternal = cloning || loadingToggleBuyer || addingSellerEmail || savingSettings;

  const sitesList = useMemo(() => {
    if (!sitesData?.adminSitesQuery) return [];
    return sitesData.adminSitesQuery.filter((site) => {
      return allowedSellerSiteIds.includes(site._id) && site.siteName !== 'Spoiler Alert Marketplace';
    });
  }, [sitesData, allowedSellerSiteIds]);

  useEffect(() => {
    TitleService.setTitles('Curated Network Listing');
    Breadcrumbs.set([
      {
        url: '/curated-network-listing',
        title: 'Curated Network Listing',
      },
    ]);
  }, []);

  const changeSite = (sites: SelectItem[]) => setSiteSelection(sites);

  const lists = listData?.adminCuratedListByCustomerQuery || { curatedLists: [], defaultList: null };

  useEffect(() => {
    if (listData?.adminCuratedListByCustomerQuery?.curatedLists.length === 0) return;
    const curatedLists = listData?.adminCuratedListByCustomerQuery.curatedLists || [];
    if (editingList) {
      const name = editingList?.name || 'All Managed Marketplace Buyers';
      const listToEdit = curatedLists.find((li) => li.name === name);
      setEditingList(listToEdit);
    } else {
      setEditingList(curatedLists[0]);
    }
    setSearchText('');
  }, [listData]);

  const changeList = (list: DistributionList<BSR>) => {
    setEditingList(list);
  };

  const { defaultList } = lists;
  const { value: sellerSiteId, text: sellerSiteName } =
    siteSelection.length > 0 ? siteSelection[0] : { value: '', text: null };

  const defaultListGrouped = useMemo(() => {
    if (defaultList) {
      return [
        ...defaultList.buyerSellerRelationships
          .reduce((bsrMap, bsr) => {
            const key = bsr.buyerSiteId;
            if (bsrMap.has(key)) {
              bsrMap.get(key)?.push(bsr);
            } else {
              bsrMap.set(key, [bsr]);
            }
            return bsrMap;
          }, new Map<string, RootBSR[]>())
          .entries(),
      ];
    }
    return [];
  }, [defaultList]);

  const curatedListMap = useMemo(() => {
    if (editingList?.buyerSellerRelationships) {
      return editingList.buyerSellerRelationships.reduce((bsrMap, bsr) => {
        const key = bsr.buyerSiteId;
        if (bsrMap.has(key)) {
          bsrMap.get(key)?.push(bsr);
        } else {
          bsrMap.set(key, [bsr]);
        }
        return bsrMap;
      }, new Map<string, BSR[]>());
    }
    return new Map<string, BSR[]>();
  }, [editingList]);

  const filteredDefaultListGroup = useMemo(() => {
    if (searchText === '') return defaultListGrouped;
    const searchRegex = new RegExp(searchText.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&'), 'i');
    return defaultListGrouped.filter(([buyerSiteId, bsrs]) => {
      const curated = curatedListMap.get(buyerSiteId);
      let emails = curated ? curated.flatMap((c) => [...c.sellerUserEmails, ...c.buyerUserEmails]) : [];
      if (emails.length === 0) {
        emails = bsrs.flatMap((b) => [...b.sellerUserEmails, ...b.buyerUserEmails]);
      }
      const { buyerSiteName } = bsrs[0];
      return searchRegex.test(buyerSiteName || '') || emails.some((e) => searchRegex.test(e));
    });
  }, [defaultListGrouped, curatedListMap, searchText]);

  const handleError = (message: string) => {
    AlertService.alert({ type: 'warning', message, autoDismiss: true, dismissDelay: 5000 });
  };

  const canSaveSettings = editingList?.name !== '';

  const saveListSettings = async (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();
    if (!editingList || !canSaveSettings) return;
    try {
      const result = await saveSettings({
        variables: {
          curatedListId: editingList._id,
          name: editingList.name,
          archived: editingList.archived,
          sellerSiteId,
        },
      });
      if (!result.data?.saveCuratedListSettings) throw new Error('Something went wrong');
      const { errors } = result.data?.saveCuratedListSettings;
      if (errors && errors.length > 0) {
        throw new Error(errors[0].message);
      }
    } catch (error) {
      handleError((error as Error).message);
    }
  };

  const cloneDefaultList = async () => {
    if (!editingList) return;
    try {
      const result = await cloneList({
        variables: {
          sellerSiteId,
          curatedListId: editingList._id,
        },
      });
      if (!result.data?.cloneCuratedList) throw new Error('Something went wrong');
      const { errors } = result.data?.cloneCuratedList;
      if (errors && errors.length > 0) {
        throw new Error(errors[0].message);
      }
    } catch (error) {
      handleError((error as Error).message);
    }
  };

  const toggleAllEnable = async () => {
    if (!editingList) return;
    try {
      const disabledBuyerSiteIds = defaultListGrouped.reduce((disabledList, [buyerSiteId]) => {
        if (!curatedListMap.has(buyerSiteId)) {
          disabledList.add(buyerSiteId);
        }
        return disabledList;
      }, new Set<string>());
      const result = await toggleBuyers({
        variables: {
          curatedListId: editingList._id,
          sellerSiteId,
          buyerSiteIds: [...disabledBuyerSiteIds],
          enable: true,
        },
      });
      if (!result.data?.toggleBuyerInCuratedList) throw new Error('Something went wrong');
      const { errors } = result.data?.toggleBuyerInCuratedList;
      if (errors && errors.length > 0) {
        throw new Error(errors[0].message);
      }
    } catch (error) {
      handleError((error as Error).message);
    }
  };

  const handleEnableAll = () => {
    if (editingList?.buyerSellerRelationships.length === 0) {
      cloneDefaultList();
    } else {
      toggleAllEnable();
    }
  };

  const handleAddSellerEmail = async (email: string) => {
    if (!editingList) return;
    try {
      setAddingSellerContact(false);
      const result = await addSellerEmail({
        variables: {
          curatedListId: editingList._id,
          sellerUserEmail: email,
        },
      });
      if (!result.data?.addSellerContact) throw new Error('Something went wrong');
      const { errors } = result.data?.addSellerContact;
      if (errors && errors.length > 0) {
        throw new Error(errors[0].message);
      }
    } catch (error) {
      handleError((error as Error).message);
    }
  };

  const handleArchiveCheck = (event: { checked: boolean }) => {
    if (!editingList) return;
    setEditingList({
      ...editingList,
      archived: event.checked,
    });
  };

  const handleNameChange = (name: string) => {
    if (!editingList) return;
    setEditingList({
      ...editingList,
      name,
    });
  };

  const handleSearch = Timing.debounce((searchText: string) => setSearchText(searchText), 200);

  const TypedAddContactIcon = AddContactIcon as FunctionComponent<{ style?: object }>;

  if (loadingInitial) return <Loading loading={true} />;

  return (
    <div className={classes.wrap}>
      <div className={classes.headerRow}>
        <MemorySelect
          containerClassName={classes.select}
          cacheId="ManagerSiteSelect"
          search
          placeholderText="Select a Site"
          onChange={changeSite}
          selectedItems={siteSelection}
        >
          {sitesList &&
            sitesList.map((site) => (
              <SelectOption key={site._id} value={site._id}>
                {site.siteName}
              </SelectOption>
            ))}
        </MemorySelect>
        {lists.curatedLists.map((cl) => {
          const link = cl.name === '';
          const secondary = !link && cl._id !== editingList?._id;
          const onClick = cl._id !== editingList?._id ? () => changeList(cl) : undefined;
          const name = link ? 'Create New' : cl.name;
          return (
            <Button secondary={secondary} link={link} key={cl._id} onClick={onClick}>
              {name}
            </Button>
          );
        })}
      </div>
      <div className={classes.workingArea}>
        <div className={classes.buyerFilter}>
          {curatedListMap.size < defaultListGrouped.length && <Button onClick={handleEnableAll}>Enable All</Button>}
          <Search placeholder="Filter Buyers" onChange={handleSearch} />
          <h4>
            {curatedListMap.size} / {defaultListGrouped.length} Buyers selected
          </h4>
          {editingList && (
            <form className={classes.listSettings} onSubmit={saveListSettings}>
              {curatedListMap.size > 0 && (
                <MiniTooltip text="Add Seller Award Contact" position="bottom">
                  <Button icon={TypedAddContactIcon} secondary onClick={() => setAddingSellerContact(true)} />
                </MiniTooltip>
              )}
              <TextInput
                value={editingList.name}
                labelText="Distribution List Name"
                className={classes.listNameField}
                onChange={handleNameChange}
                id="list-name"
                autocomplete={false}
              />
              <CheckboxWithLabel
                id={'checkbox-archive'}
                checked={editingList.archived}
                label="Archive List"
                onChecked={handleArchiveCheck}
                value={'archived'}
                disabled={false}
                testId="archive-check"
              />
              <Button disabled={!canSaveSettings} type="submit" onClick={saveListSettings}>
                Save
              </Button>
            </form>
          )}
          <LoadingLine loading={loadingInternal} position="bottom" />
        </div>
        {filteredDefaultListGroup &&
          editingList &&
          sellerSiteId &&
          editingList?.saved &&
          filteredDefaultListGroup.map(([buyerSiteId, bsrs]) => (
            <BuyerSelection
              key={`${buyerSiteId}:${editingList._id}`}
              sellerSiteId={sellerSiteId}
              buyerSiteId={buyerSiteId}
              defaultBSRs={bsrs}
              curatedBSRs={curatedListMap.get(buyerSiteId)}
              curatedListId={editingList._id}
              handleError={handleError}
            />
          ))}
        {loadingLists && (
          <div className={classes.noCuratedList}>
            <LoadingLarge loading={loadingLists} />
          </div>
        )}
        {filteredDefaultListGroup.length === 0 && defaultListGrouped.length > 0 && (
          <div className={classes.noCuratedList}>
            <div className={classes.noCuratedListContent}>
              No results for <em>{searchText}</em>
            </div>
          </div>
        )}
        {editingList && !editingList.saved && (
          <div className={classes.noCuratedList}>
            <div className={classes.noCuratedListContent}>Click Save before adding contacts</div>
          </div>
        )}
      </div>
      <EditContactModal
        open={addingSellerContact}
        onHide={() => setAddingSellerContact(false)}
        handleSubmit={handleAddSellerEmail}
        loading={addingSellerEmail}
        email={''}
        selectedContactType="SELLER"
      />
    </div>
  );
};

export default CuratedDistributionLists;
